'use client';

import NextImage from 'next/image';
import { motion } from 'framer-motion';
import CmsImage from '@/components/ui/CmsImage';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { pickButton, stripHtml } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getStrapiMediaUrl } from '@/lib/strapi-media-url';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import type { CommunityNeevSection } from '@/types/community';

interface CommunityNeevSectionViewProps {
  zone: CommunityNeevSection;
}

type NeevImage = NonNullable<CommunityNeevSection['imageGrid']>[number] & {
  imageUrl: string | null;
};

function NeevImageGrid({
  featured,
  secondary,
}: {
  featured: NeevImage | undefined;
  secondary: NeevImage[];
}) {
  return (
    <>
      <div className="flex flex-col gap-3 sm:gap-4 lg:hidden">
        {featured?.imageUrl ? (
          <motion.div
            className="relative h-[200px] overflow-hidden rounded-2xl shadow-sm sm:h-[240px]"
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          >
            <CmsImage
              src={featured.imageUrl}
              alt={featured.media?.alternativeText || featured.heading || ''}
              fill
              className="object-cover"
              sizes="100vw"
              fallbackSrc={MEDIA_PLACEHOLDER}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            {featured.heading ? (
              <span className="absolute bottom-4 left-4 text-[11px] font-bold uppercase tracking-[0.2em] text-white/80">
                {featured.heading}
              </span>
            ) : null}
          </motion.div>
        ) : null}

        {secondary.length > 0 ? (
          <div className="grid grid-cols-3 gap-3">
            {secondary.map((item, i) => (
              <motion.div
                key={item.id}
                className="relative aspect-[3/4] overflow-hidden rounded-xl shadow-sm sm:rounded-2xl"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.6,
                  delay: 0.1 * (i + 1),
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <CmsImage
                  src={item.imageUrl}
                  alt={item.media?.alternativeText || item.heading || ''}
                  fill
                  className="object-cover"
                  sizes="33vw"
                  fallbackSrc={MEDIA_PLACEHOLDER}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                {item.heading ? (
                  <span className="absolute bottom-2 left-2 right-2 text-[10px] font-bold uppercase leading-tight tracking-[0.15em] text-white/80 sm:bottom-3 sm:left-3 sm:text-[11px] sm:tracking-[0.2em]">
                    {item.heading}
                  </span>
                ) : null}
              </motion.div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="hidden h-[480px] grid-cols-2 grid-rows-3 gap-3 lg:grid">
        {featured?.imageUrl ? (
          <motion.div
            className="relative row-span-3 overflow-hidden rounded-2xl shadow-sm"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <CmsImage
              src={featured.imageUrl}
              alt={featured.media?.alternativeText || featured.heading || ''}
              fill
              className="object-cover transition-transform duration-700 hover:scale-105"
              sizes="25vw"
              fallbackSrc={MEDIA_PLACEHOLDER}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            {featured.heading ? (
              <span className="absolute bottom-4 left-4 text-[11px] font-bold uppercase tracking-[0.2em] text-white/80">
                {featured.heading}
              </span>
            ) : null}
          </motion.div>
        ) : null}

        {secondary.map((item, i) => (
          <motion.div
            key={item.id}
            className="relative overflow-hidden rounded-2xl shadow-sm"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 * (i + 1), ease: [0.22, 1, 0.36, 1] }}
          >
            <CmsImage
              src={item.imageUrl}
              alt={item.media?.alternativeText || item.heading || ''}
              fill
              className="object-cover transition-transform duration-700 hover:scale-105"
              sizes="20vw"
              fallbackSrc={MEDIA_PLACEHOLDER}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            {item.heading ? (
              <span className="absolute bottom-3 left-3 text-[11px] font-bold uppercase tracking-[0.2em] text-white/80">
                {item.heading}
              </span>
            ) : null}
          </motion.div>
        ))}
      </div>
    </>
  );
}

export default function CommunityNeevSectionView({ zone }: CommunityNeevSectionViewProps) {
  const images = (zone.imageGrid ?? [])
    .map((item) => ({
      ...item,
      imageUrl: getStrapiMediaUrl(item.media, 'large'),
    }))
    .filter((item) => Boolean(item.imageUrl));

  const primaryButton = pickButton(zone.button, 'Solid') ?? zone.button?.[0];
  const buttonUrl = primaryButton?.buttonUrl?.trim();
  const isExternal = buttonUrl?.startsWith('http');

  if (!zone.heading && images.length === 0) return null;

  const featured = images[0];
  const secondary = images.slice(1, 4);

  const linkProps = getCmsButtonLinkProps(primaryButton, isExternal ? '_blank' : undefined);

  return (
    <section className="bg-stone-50 px-4 sm:px-6 md:px-16 lg:px-24">
      <div className="container mx-auto">
        <motion.div
          className="mb-8 flex justify-center sm:mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-5% 0px' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <NextImage
            src="/images/neev-logo.png"
            alt="NEEV — Wonder Cement CSR Initiative"
            width={400}
            height={140}
            className="h-20 w-auto object-contain mix-blend-multiply sm:h-32 lg:h-36"
          />
        </motion.div>

        <motion.div
          className="mb-8 h-px bg-stone-200 sm:mb-10"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          style={{ originX: 0.5 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        />

        <div className="grid grid-cols-1 items-center gap-8 sm:gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            {zone.eyebrowHeading ? (
              <motion.p
                className="mb-3 text-[0.65rem] font-semibold uppercase tracking-[0.28em] text-stone-400"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                {zone.eyebrowHeading}
              </motion.p>
            ) : null}

            {zone.heading ? (
              <motion.h2
                className="mb-4 text-[1.75rem] font-bold leading-[1.12] tracking-tight text-[#1a1a1a] sm:mb-5 sm:text-4xl md:text-[2.75rem] lg:text-5xl"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                {zone.heading}
              </motion.h2>
            ) : null}

            {zone.subHeading ? (
              <motion.p
                className="mb-8 max-w-md text-sm leading-7 text-stone-600 sm:mb-10 sm:text-[15px] sm:leading-8"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {stripHtml(zone.subHeading)}
              </motion.p>
            ) : null}

            {primaryButton?.buttonText?.trim() && buttonUrl ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              >
                <SwapTextButton
                  text={primaryButton.buttonText.trim()}
                  hoverText={primaryButton.buttonText.trim()}
                  aria-label={primaryButton.ariaLabel?.trim() || primaryButton.buttonText.trim()}
                  href={buttonUrl}
                  {...linkProps}
                  className="!rounded-full !px-8 !py-4 shadow-[0_4px_20px_rgba(237,28,34,0.35)]"
                />
              </motion.div>
            ) : null}
          </div>

          {images.length > 0 ? <NeevImageGrid featured={featured} secondary={secondary} /> : null}
        </div>
      </div>
    </section>
  );
}
